import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { AppDispatch } from "index";
import { Spinner, Alert, Card, Button, Row, Col } from "react-bootstrap";
import { Select, MenuItem } from "@mui/material";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import { MecordColors } from "Constants/Color";
import { fetchServiceEnquiryById } from "slices/enquiry/thunk";
import { EnquiryDataProps } from "../../types";

// Modular View Components
import EnquiryViewComponent from "../../Components/EnquiryViewComponent";
import LoadingSpinner from "components/LoadingSpinner";

/**
 * ServiceEnquiryViewV2 Component
 */
const ServiceEnquiryViewV2: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [enquiry, setEnquiry] = useState<EnquiryDataProps | null>(null);

  const fetchSingleEnquiryData = async () => {
    if (!id) return;
    setEnquiry(null);
    setFetchError(null);
    setIsLoading(true);

    try {
      const resData = await dispatch(fetchServiceEnquiryById(id)).unwrap();
      const details = resData?.data?.data || resData?.data || null;
      setEnquiry(details);
    } catch (err: any) {
      console.error("Error fetching service enquiry:", err);
      setFetchError(err?.message || "Failed to fetch service enquiry details.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSingleEnquiryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  const handleConvertToQuotation = () => {
    if (enquiry?.id) {
      navigate(`/enquiry/service/create-quotation/${enquiry.id}`);
    }
  };


  return (
    <PageContainer>
      <PageBreadcrumb
        title="View Service Enquiry"
        titleLink="/enquiry/service/enquiry_log"
        breadcrumbs={[
          { label: "Service Enquiry", to: "/enquiry/service/enquiry_log" },
          { label: "View Service Enquiry", to: "" },
        ]}
        backNavigationPath={-1}
      />

      <hr />

      {isLoading ? (
        <LoadingSpinner />
      ) : (fetchError || !enquiry) ? (
        <div className="p-4">
          <Alert variant={fetchError ? "danger" : "warning"}>
            {fetchError || "No service enquiry data found."}
          </Alert>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Back to List
          </Button>
        </div>
      ) : (
        <div className="p-1">
          <EnquiryViewComponent
            details={enquiry}
            type="service"
            onConvertToQuotation={handleConvertToQuotation}
          />
        </div>
      )}
    </PageContainer>
  );
};

export default ServiceEnquiryViewV2;
